import { useEffect, useState } from "react";
import type { SelectorEmoji } from "../types/emojiTypes";

const SKIN_TONE_MODIFIERS = /[\u{1F3FB}-\u{1F3FF}]/gu;
const VARIATION_SELECTOR_16 = /\uFE0F/gu;

/**
 * Canonical comparison key — MUST mirror the backend's emojiKey()
 * (n450s_backend/utils/emoji.ts): NFC + trim + strip skin tones + strip VS16.
 * The backend now serves canonical emoji forms (used-emojis, reusedEmojis),
 * so raw string comparison against user-typed bytes (👍🏽 vs 👍, ❤ vs ❤️)
 * silently fails — always compare via this key.
 */
export function emojiKey(str: string): string {
  return (str ?? "")
    .trim()
    .normalize("NFC")
    .replace(SKIN_TONE_MODIFIERS, "")
    .replace(VARIATION_SELECTOR_16, "");
}

/** Tab icons for the 9 Unicode CLDR groups (iOS-keyboard style). */
export const CATEGORY_ICONS: Record<string, string> = {
  "Smileys & Emotion": "😀",
  "People & Body": "👋",
  "Animals & Nature": "🐻",
  "Food & Drink": "🍔",
  "Travel & Places": "🚗",
  Activities: "⚽",
  Objects: "💡",
  Symbols: "🔣",
  Flags: "🏁",
  // legacy v1 taxonomy — served until `pnpm emoji:migrate` runs on that DB
  "People & Gestures": "👋",
  Animals: "🐻",
  "Nature & Weather": "🌿",
  "Activities & Sports": "⚽",
  Imported: "📦",
};

export function categoryIcon(category: string): string {
  return CATEGORY_ICONS[category] ?? "✨";
}

export function selectorEmojiMatches(
  e: SelectorEmoji,
  category: string,
  q: string,
): boolean {
  if (!q) return true;
  const needle = q.trim().toLowerCase();
  if (!needle) return true;
  return (
    e.e === q.trim() ||
    category.toLowerCase().includes(needle) ||
    (e.n ?? "").toLowerCase().includes(needle) ||
    (e.owner ?? "").toLowerCase().includes(needle)
  );
}

/** Matches the project's mobile breakpoint (max-[700px] in tailwind usage). */
export function useIsNarrow(maxWidth = 700): boolean {
  const [narrow, setNarrow] = useState<boolean>(() =>
    typeof window !== "undefined"
      ? window.matchMedia(`(max-width: ${maxWidth}px)`).matches
      : false,
  );
  useEffect(() => {
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const onChange = (ev: MediaQueryListEvent): void => setNarrow(ev.matches);
    mq.addEventListener("change", onChange);
    setNarrow(mq.matches);
    return () => mq.removeEventListener("change", onChange);
  }, [maxWidth]);
  return narrow;
}
